/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd'
import LockIcon from '@mui/icons-material/Lock'
import ErrorIcon from '@mui/icons-material/Error'
import Tooltip from '@mui/material/Tooltip'
import CheckCircle from '@mui/icons-material/CheckCircle'
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import {
  AlertLevel,
  AlertType,
  BoardAlertFragment,
  BoardItemFragment
} from '../../../../generated/graphql'
import { captionTextCss, subtitle1Css, tertiaryBodyCss } from '../../../../lib/styles/typography'
import {
  alternateGray,
  errorIconColor,
  offsetGray2,
  primaryGreen,
  primaryYellow,
  secondaryTextColor,
  lightBlue
} from '../../../../lib/styles/colors'
import { oneDpShadowCss } from '../../../../lib/styles/shadows'
import { formatCredits } from '../../../../lib/utils'
import { srOnlyCss } from '../../../../lib/styles/cssStyles'

export type PlanItemCardProps = {
  /** List of alerts belonging to the item */
  alerts: Array<BoardAlertFragment>
  /** Label to use for course */
  courseLabel: string
  /** Label to show next to credit value on card */
  creditsLabel?: string
  /** Current index of the card within PlanBoard DnD state */
  index: number
  /** Current Plan Item */
  item: BoardItemFragment
  /** Callback event that occurs when clicking or pressing Enter on the card */
  onOpen: () => void
  /** Whether the plan is read-only */
  readOnly?: boolean
  /** Label to use for registered */
  registeredLabel: string
  /** Show Grades on the plan item; controlled by feature toggle */
  showGrades?: boolean
  /** Show withdrawn alert / status on the plan item; controlled by feature toggle */
  showWithdrawnAlert?: boolean
  /** Label to use for term */
  termLabel: string
}

const defaultProps: Pick<
  PlanItemCardProps,
  'creditsLabel' | 'readOnly' | 'showGrades' | 'showWithdrawnAlert'
> = {
  creditsLabel: '',
  readOnly: false,
  showGrades: false,
  showWithdrawnAlert: false
}

const titleCss = css`
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 2px;
`
const titleHeaderCss = css`
  ${subtitle1Css}
  font-weight: 500;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
const creditsCss = css`
  ${tertiaryBodyCss}
  color: ${secondaryTextColor};
  line-height: 24px;
`
const subTextCss = css`
  max-width: 325px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
export const subTitleTextCss = css`
  ${tertiaryBodyCss}
  ${subTextCss}
`
export const placeholderCss = css`
  border-left: 4px ${primaryYellow} solid;

  & > div {
    margin-left: -4px;
  }
`
const alertBorderCss = css`
  border-left: 4px ${errorIconColor} solid;

  & > div {
    margin-left: -4px;
  }
`
const alertTextBorderCss = css`
  svg {
    font-size: 14px;
    color: ${errorIconColor};
    padding-right: 6px;
    padding-top: 1px;
  }
  ${captionTextCss}
  color: ${alternateGray};
  display: flex;
  ${subTextCss}
`

const PlanItemCard = ({
  alerts,
  courseLabel,
  creditsLabel,
  index,
  item,
  onOpen,
  readOnly,
  registeredLabel,
  showGrades,
  showWithdrawnAlert,
  termLabel
}: PlanItemCardProps) => {
  const {
    campusDescription,
    career,
    courseNumber,
    creditsMax,
    creditsMin,
    enrollmentStatus,
    externalCourseId,
    gradeReceived,
    isCoursePlaceholder,
    locked,
    nonCourseCreditsMax,
    nonCourseCreditsMin,
    nonCourseTitle,
    subjectId,
    termGradeReceived,
    title
  } = item
  let minCredits = creditsMin
  let maxCredits = creditsMax
  if (!courseNumber && !subjectId) {
    minCredits = nonCourseCreditsMin
    maxCredits = nonCourseCreditsMax
  }
  const formattedCredits = formatCredits(minCredits || undefined, maxCredits || undefined)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      onOpen()
    }
  }

  const placeholder = isCoursePlaceholder
  const hasCourseAndSubject = courseNumber && subjectId
  const description =
    (hasCourseAndSubject && title) ||
    (placeholder && `Select ${courseLabel.toLowerCase()} for placeholder`) ||
    null

  const alertMap: { [key: string]: BoardAlertFragment | undefined } = {}
  let warningAlertCount = 0

  // Filter out Withdrawn Alerts if the setting says not to show them
  let alertsToDisplay = alerts
  if (!showWithdrawnAlert) {
    alertsToDisplay = alerts.filter((alert) => alert.alertType !== AlertType.WithdrawnCourse)
  }

  alertsToDisplay.forEach((alert) => {
    if (!alertMap[alert.alertType]) {
      alertMap[alert.alertType] = alert
    }
    if (alert.level === AlertLevel.Warning) {
      warningAlertCount += 1
    }
  })

  const innerCard = (
    <div
      // meta-data attributes strictly for internal identification purposes
      external-course-id={externalCourseId || 'null'}
      campus-description={campusDescription || 'null'}
      career-id={career || 'null'}
    >
      <div css={titleCss}>
        <div className="title" css={titleHeaderCss}>
          {hasCourseAndSubject ? (
            `${subjectId} ${courseNumber}`
          ) : (
            <Tooltip aria-label={undefined} tabIndex={0} title={nonCourseTitle || ''}>
              <span>{nonCourseTitle}</span>
            </Tooltip>
          )}
        </div>
        {formattedCredits && (
          <div css={creditsCss}>
            {formattedCredits} {creditsLabel}
          </div>
        )}
      </div>
      <div>
        <div css={titleCss}>
          {hasCourseAndSubject ? (
            <Tooltip aria-label={undefined} tabIndex={0} title={description || ''}>
              <div css={subTitleTextCss}>{description}</div>
            </Tooltip>
          ) : (
            <div css={subTitleTextCss}>{description}</div>
          )}
          {locked && <LockIcon />}
        </div>
        {alertMap[AlertType.EnrolledCourse] && enrollmentStatus && (
          <div css={alertTextBorderCss}>
            <Tooltip
              aria-label={undefined}
              tabIndex={0}
              title={`${registeredLabel} for ${enrollmentStatus.termDescription}`}
            >
              <CircleOutlinedIcon style={{ color: lightBlue }} />
            </Tooltip>
            {`${registeredLabel} for ${enrollmentStatus.termDescription}`}
            <div css={srOnlyCss}>
              {registeredLabel} for {enrollmentStatus.termDescription}
            </div>
          </div>
        )}
        {alertMap[AlertType.ItemMeetsRequirement] && (
          <div css={alertTextBorderCss}>
            <Tooltip aria-label={undefined} tabIndex={0} title={`${courseLabel} meets requirement`}>
              <CheckCircle style={{ color: primaryGreen }} />
            </Tooltip>
            <div css={srOnlyCss}>{courseLabel} meets requirement</div>
            {showGrades && gradeReceived && termGradeReceived && (
              <div>
                Completed in: {termGradeReceived}, Grade: {gradeReceived}
              </div>
            )}
          </div>
        )}
        {alertMap[AlertType.ItemDoesNotMeetRequirement] && (
          <div css={alertTextBorderCss}>
            <Tooltip
              aria-label={undefined}
              tabIndex={0}
              title={`${courseLabel} did not meet any requirement`}
            >
              <HighlightOffIcon fontSize="small" style={{ color: errorIconColor }} />
            </Tooltip>
            <div css={srOnlyCss}>{courseLabel} did not meet any requirement</div>
            {showGrades && gradeReceived && termGradeReceived && (
              <div>
                {termGradeReceived}, Grade: {gradeReceived}
              </div>
            )}
          </div>
        )}
        {alertMap[AlertType.InProgressCourse] && (
          <div css={alertTextBorderCss}>
            <Tooltip aria-label={undefined} tabIndex={0} title={`${courseLabel} is in progress`}>
              <CheckCircleOutlineIcon fontSize="small" style={{ color: lightBlue }} />
            </Tooltip>
            <div css={srOnlyCss}>{courseLabel} is in progress</div>
            {showGrades && termGradeReceived && <div>In Progress in: {termGradeReceived}</div>}
          </div>
        )}
        {alertMap[AlertType.WithdrawnCourse] && (
          <div css={alertTextBorderCss}>
            <Tooltip aria-label={undefined} tabIndex={0} title={`${courseLabel} is withdrawn`}>
              <HighlightOffIcon fontSize="small" style={{ color: errorIconColor }} />
            </Tooltip>
            <div css={srOnlyCss}>{courseLabel} is withdrawn</div>
            {showGrades && gradeReceived && termGradeReceived && (
              <div>
                Attempted in: {termGradeReceived}, Grade: {gradeReceived}
              </div>
            )}
          </div>
        )}
        {alertMap[AlertType.DuplicateCourse] && (
          <div css={alertTextBorderCss}>
            <ErrorIcon /> Duplicate {courseLabel}
          </div>
        )}
        {alertMap[AlertType.InactiveCourse] && (
          <div css={alertTextBorderCss}>
            <ErrorIcon /> This {courseLabel.toLowerCase()} is no longer offered or available
          </div>
        )}
        {alertMap[AlertType.Requisite] && (
          <div css={alertTextBorderCss}>
            <ErrorIcon /> This {courseLabel.toLowerCase()} has a requisite
          </div>
        )}
        {alertMap[AlertType.Unavailable] && (
          <div css={alertTextBorderCss}>
            <ErrorIcon /> {courseLabel} may not be available for this {termLabel.toLowerCase()}
          </div>
        )}
      </div>
    </div>
  )

  const ariaTitle =
    subjectId && courseNumber ? `${subjectId} ${courseNumber} ${title}` : nonCourseTitle
  const lockedMessage =
    'This item is locked and cannot be moved or deleted. Hit enter to view the details of this item.'
  const unlockedMessage = `Hit enter to edit this item or to move it to another ${termLabel.toLowerCase()}.`
  const ariaLabel = `You are currently on item ${ariaTitle}. ${
    locked ? lockedMessage : unlockedMessage
  }`

  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={readOnly || locked}>
      {(provided: DraggableProvided, { isDragging }: DraggableStateSnapshot) => {
        const cardCss = css`
          ${isDragging && 'outline: 2px solid rgba(63, 81, 181, 0.7);'}
          background: ${locked ? `${offsetGray2}` : 'white'};
          border-radius: 2px;
          box-sizing: border-box;
          margin-bottom: 12px;
          min-height: 72px;
          padding: 6px 8px;

          &:focus,
          &:hover {
            .title {
              color: ${alternateGray};
            }
          }
        `
        const customProvided = { ...provided }
        // removes the DnD onFocus messaging
        // https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/screen-reader.md
        if (customProvided.dragHandleProps) {
          customProvided.dragHandleProps['aria-describedby'] = ''
        }

        return (
          <div
            aria-label={ariaLabel}
            css={[
              cardCss,
              oneDpShadowCss,
              placeholder && placeholderCss,
              !!warningAlertCount && alertBorderCss
            ]}
            onClick={onOpen}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            ref={customProvided.innerRef}
            {...customProvided.draggableProps}
            {...customProvided.dragHandleProps}
          >
            {innerCard}
          </div>
        )
      }}
    </Draggable>
  )
}

PlanItemCard.defaultProps = defaultProps

export default PlanItemCard
